import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";

/**
 * Zuschnitt-Modal für Team-Bilder.
 * Aspect-Ratio: 1:1 (quadratisch), passt zu den runden Team-Fotos.
 *
 * Props:
 *   imageSrc  – Data-URL des ausgewählten Bildes
 *   onCancel  – wird bei Abbruch aufgerufen
 *   onCropped(dataUrl) – wird nach Zuschnitt mit dem finalen Bild-Data-URL aufgerufen
 */
export default function ImageCropper({ imageSrc, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const applyCrop = useCallback(async () => {
    if (!croppedAreaPixels) return;
    const dataUrl = await getCroppedImage(imageSrc, croppedAreaPixels);
    onCropped(dataUrl);
  }, [imageSrc, croppedAreaPixels, onCropped]);

  return (
    <div className="cropper-overlay" data-testid="image-cropper" role="dialog" aria-modal="true">
      <div className="cropper-modal">
        <div className="cropper-canvas">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="cropper-controls">
          <label className="cropper-zoom-label">
            Zoom
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              data-testid="cropper-zoom"
            />
          </label>
          <div className="cropper-actions">
            <button type="button" className="admin-btn admin-btn-ghost" onClick={onCancel} data-testid="cropper-cancel">
              Abbrechen
            </button>
            <button type="button" className="admin-btn admin-btn-primary" onClick={applyCrop} data-testid="cropper-apply">
              Zuschnitt übernehmen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Bild-Ausschnitt als Data-URL zurückgeben
async function getCroppedImage(src, pixelCrop) {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  const size = Math.min(pixelCrop.width, 640);
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    img,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, size, size
  );
  return canvas.toDataURL("image/jpeg", 0.9);
}
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
